import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../../store';
import { 
  Search, 
  ShoppingCart, 
  Plus,
  Grid,
  List,
  Package,
  Filter,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  Wallet,
  Percent,
} from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DealerShop() {
  const { token, user } = useAuthStore();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [cartCount, setCartCount] = useState(0);
  const [addingToCart, setAddingToCart] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const dealerIskonto = user?.iskonto_orani || 0;
  const [banners, setBanners] = useState([]);
  const [shopCategories, setShopCategories] = useState([]);
  const [bannerIdx, setBannerIdx] = useState(0);
  const bannerTimerRef = useRef(null);
  const [cariBakiye, setCariBakiye] = useState(0);

  const getNetPrice = (price) => {
    if (dealerIskonto > 0) return Math.round(price * (1 - dealerIskonto / 100) * 100) / 100;
    return price;
  };

  useEffect(() => {
    fetchProducts();
    fetchCartCount();
    fetchShopData();
    fetchDealerStats();
  }, [search, category]);

  const fetchDealerStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dealer/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setCariBakiye(res.data?.stats?.cari_bakiye || 0);
    } catch {}
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      params.append('limit', '100');
      
      const res = await axios.get(`${API_URL}/api/products?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(res.data.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCartCount = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCartCount(res.data.item_count || 0);
    } catch (err) {
      console.error('Error fetching cart:', err);
    }
  };

  const fetchShopData = async () => {
    try {
      const [bannersRes, catsRes] = await Promise.all([
        axios.get(`${API_URL}/api/shop/banners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/shop/categories`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setBanners(bannersRes.data || []);
      setShopCategories((catsRes.data || []).filter(c => !c.parent_id && c.image_url));
    } catch (err) {
      console.error('Error fetching shop data:', err);
    }
  };

  // Banner auto-slide
  useEffect(() => {
    if (banners.length > 1) {
      bannerTimerRef.current = setInterval(() => {
        setBannerIdx(prev => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(bannerTimerRef.current);
    }
  }, [banners.length]);

  const addToCart = async (productId, qty = 1) => {
    try {
      setAddingToCart(productId);
      await axios.post(`${API_URL}/api/cart/items?product_id=${productId}&quantity=${qty}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCartCount();
      toast.success('Ürün sepete eklendi');
      setSelectedProduct(null);
      setQuantity(1);
    } catch (err) {
      console.error('Error adding to cart:', err);
      toast.error(err.response?.data?.detail || 'Ürün eklenemedi');
    } finally {
      setAddingToCart(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  // Get unique categories
  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  return (
    <div className="space-y-4 sm:space-y-6 pb-20" data-testid="dealer-shop">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Sipariş Ver</h1>
          <p className="text-sm text-gray-500">Ürünleri inceleyin ve sepetinize ekleyin</p>
        </div>
        <Link
          to="/dealer/cart"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#141a3d]"
          data-testid="view-cart-btn"
        >
          <ShoppingCart className="w-4 h-4" />
          Sepetim ({cartCount})
        </Link>
      </div>

      {/* Cari Borç & İskonto Bilgi Kartları */}
      <div className="grid grid-cols-2 gap-3 pb-2">
        <Link to="/dealer/account" className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 flex items-center gap-3 hover:shadow-md hover:border-gray-200 transition-all" data-testid="cari-bakiye-card">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
            <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] sm:text-xs text-gray-400 font-medium">Cari Borç</p>
            <p className={`text-base sm:text-lg font-bold tabular-nums ${cariBakiye > 0 ? 'text-[#1a224f]' : 'text-gray-900'}`}>
              {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(cariBakiye)}
            </p>
          </div>
        </Link>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 flex items-center gap-3" data-testid="iskonto-card">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
            <Percent className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] sm:text-xs text-gray-400 font-medium">İskonto Oranı</p>
            <p className="text-base sm:text-lg font-bold text-gray-900">%{dealerIskonto}</p>
          </div>
        </div>
      </div>

      {/* Banner Slider */}
      {banners.length > 0 && (
        <div className="relative rounded-xl overflow-hidden shadow-sm mt-2" data-testid="shop-banner">
          <div className="relative h-36 sm:h-48 md:h-56">
            {banners.map((banner, idx) => (
              <div
                key={banner.id}
                className={`absolute inset-0 transition-opacity duration-700 ${idx === bannerIdx ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              >
                <img src={banner.image_url} alt={banner.title || ''} className="w-full h-full object-cover" />
                {banner.title && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                    <p className="text-white font-semibold text-sm sm:text-base">{banner.title}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          {banners.length > 1 && (
            <>
              <button onClick={() => setBannerIdx((bannerIdx - 1 + banners.length) % banners.length)} className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/80 hover:bg-white shadow-sm"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setBannerIdx((bannerIdx + 1) % banners.length)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/80 hover:bg-white shadow-sm"><ChevronRight className="w-4 h-4" /></button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                {banners.map((_, idx) => (
                  <button key={idx} onClick={() => setBannerIdx(idx)} className={`w-2 h-2 rounded-full transition-all ${idx === bannerIdx ? 'bg-white w-5' : 'bg-white/50'}`} />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Category Images */}
      {shopCategories.length > 0 && (
        <div data-testid="shop-categories" className="pt-2">
          <h2 className="text-base font-bold text-gray-900 mb-3">Urun Kategorileri</h2>
          <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 scrollbar-hide">
            {shopCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setCategory(category === cat.name ? '' : cat.name)}
                className={`flex-shrink-0 group cursor-pointer transition-all ${category === cat.name ? 'scale-[1.02]' : ''}`}
                data-testid={`shop-cat-${cat.id}`}
              >
                <div className={`w-24 h-24 sm:w-36 sm:h-36 rounded-xl sm:rounded-2xl overflow-hidden border-2 transition-all ${category === cat.name ? 'border-[#3a4680] shadow-lg shadow-[#3a4680]/20' : 'border-transparent shadow-sm group-hover:shadow-md'}`}>
                  <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
                </div>
                <p className={`text-center text-xs sm:text-sm font-medium mt-1.5 transition-colors ${category === cat.name ? 'text-[#1a224f]' : 'text-gray-700'}`}>{cat.name}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Ürün adı, barkod veya yazar ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3a4680]/20 focus:border-[#5b67a0] shadow-sm"
            data-testid="search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          {categories.length > 0 && (
            <Select value={category || '_all_'} onValueChange={(val) => setCategory(val === '_all_' ? '' : val)}>
              <SelectTrigger className="w-auto min-w-[140px] h-10 px-3 border-gray-200 rounded-xl text-sm bg-white shadow-sm">
                <SelectValue placeholder="Tüm Kategoriler" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">Tüm Kategoriler</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-[#eef0f7] text-[#1a224f]' : 'text-gray-400 hover:bg-gray-50'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-[#eef0f7] text-[#1a224f]' : 'text-gray-400 hover:bg-gray-50'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Products */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a224f]"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ürün Bulunamadı</h3>
          <p className="text-gray-500">
            {search || category ? 'Arama kriterlerinize uygun ürün bulunamadı.' : 'Henüz ürün eklenmemiş.'}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {products.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group"
              data-testid={`product-${product.barcode}`}
            >
              {/* Image */}
              <div 
                className="aspect-[3/4] bg-gray-50 relative cursor-pointer"
                onClick={() => setSelectedProduct(product)}
              >
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-12 h-12 text-gray-300" />
                  </div>
                )}
                {product.stock === 0 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="bg-[#1a224f] text-white px-2 py-1 text-xs font-medium rounded">
                      Stokta Yok
                    </span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-3">
                <h3 
                  className="font-medium text-gray-900 text-sm line-clamp-2 mb-1 cursor-pointer hover:text-[#1a224f]"
                  onClick={() => setSelectedProduct(product)}
                >
                  {product.name}
                </h3>
                {product.author && (
                  <p className="text-xs text-gray-500 mb-2">{product.author}</p>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    {dealerIskonto > 0 ? (
                      <>
                        <p className="text-xs text-gray-400 line-through">{formatCurrency(product.price)}</p>
                        <p className="font-bold text-[#1a224f]">{formatCurrency(getNetPrice(product.price))}</p>
                      </>
                    ) : (
                      <p className="font-bold text-[#1a224f]">{formatCurrency(product.price)}</p>
                    )}
                  </div>
                  {product.stock > 0 && (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="1"
                        max={product.stock}
                        defaultValue={1}
                        className="w-12 text-center text-xs border border-gray-200 rounded-lg py-1 focus:outline-none focus:ring-1 focus:ring-[#5b67a0]"
                        data-testid={`qty-input-${product.barcode}`}
                        onClick={(e) => e.stopPropagation()}
                        id={`qty-${product.id}`}
                      />
                      <button
                        onClick={() => {
                          const input = document.getElementById(`qty-${product.id}`);
                          const qty = parseInt(input?.value) || 1;
                          addToCart(product.id, qty);
                        }}
                        disabled={addingToCart === product.id}
                        className="p-2 bg-[#d8dcee] text-[#1a224f] rounded-lg hover:bg-[#b3bbdc] transition-colors disabled:opacity-50"
                        data-testid={`add-to-cart-${product.barcode}`}
                      >
                        {addingToCart === product.id ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow"
              data-testid={`product-${product.barcode}`}
            >
              <div className="flex gap-4">
                {/* Image */}
                <div 
                  className="w-20 h-28 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer"
                  onClick={() => setSelectedProduct(product)}
                >
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 
                    className="font-medium text-gray-900 cursor-pointer hover:text-[#1a224f]"
                    onClick={() => setSelectedProduct(product)}
                  >
                    {product.name}
                  </h3>
                  {product.author && (
                    <p className="text-sm text-gray-500">{product.author}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">Barkod: {product.barcode}</p>
                  <div className="flex items-center gap-4 mt-2">
                    {dealerIskonto > 0 ? (
                      <>
                        <span className="text-xs text-gray-400 line-through">{formatCurrency(product.price)}</span>
                        <p className="font-bold text-[#1a224f]">{formatCurrency(getNetPrice(product.price))}</p>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">-%{dealerIskonto}</span>
                      </>
                    ) : (
                      <p className="font-bold text-[#1a224f]">{formatCurrency(product.price)}</p>
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      product.stock > 0 ? 'bg-green-100 text-green-700' : 'bg-[#d8dcee] text-[#141a3d]'
                    }`}>
                      {product.stock > 0 ? `${product.stock} Adet` : 'Stokta Yok'}
                    </span>
                  </div>
                </div>

                {/* Add to Cart */}
                {product.stock > 0 && (
                  <div className="self-center flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max={product.stock}
                      defaultValue={1}
                      className="w-14 text-center text-sm border border-gray-200 rounded-lg py-1.5 focus:outline-none focus:ring-1 focus:ring-[#5b67a0]"
                      data-testid={`list-qty-input-${product.barcode}`}
                      id={`list-qty-${product.id}`}
                    />
                    <button
                      onClick={() => {
                        const input = document.getElementById(`list-qty-${product.id}`);
                        const qty = parseInt(input?.value) || 1;
                        addToCart(product.id, qty);
                      }}
                      disabled={addingToCart === product.id}
                      className="px-4 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#141a3d] transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {addingToCart === product.id ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          Ekle
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 pb-16 sm:pb-0" onClick={() => { setSelectedProduct(null); setQuantity(1); }}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[75vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between z-10">
              <h2 className="text-base font-bold text-gray-900">Urun Detayi</h2>
              <button
                onClick={() => { setSelectedProduct(null); setQuantity(1); }}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4">
              <div className="space-y-4">
                {/* Image - compact */}
                {selectedProduct.image_url ? (
                  <img
                    src={selectedProduct.image_url}
                    alt={selectedProduct.name}
                    className="w-full h-40 object-contain rounded-xl bg-gray-50"
                  />
                ) : (
                  <div className="h-32 bg-gray-50 rounded-xl flex items-center justify-center">
                    <Package className="w-12 h-12 text-gray-300" />
                  </div>
                )}

                {/* Info */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{selectedProduct.name}</h3>
                  {selectedProduct.author && (
                    <p className="text-sm text-gray-500">{selectedProduct.author}</p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    {dealerIskonto > 0 ? (
                      <>
                        <p className="text-xs text-gray-400 line-through">{formatCurrency(selectedProduct.price)}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xl font-bold text-[#1a224f]">{formatCurrency(getNetPrice(selectedProduct.price))}</p>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700">%{dealerIskonto}</span>
                        </div>
                      </>
                    ) : (
                      <p className="text-xl font-bold text-[#1a224f]">{formatCurrency(selectedProduct.price)}</p>
                    )}
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    selectedProduct.stock > 0 ? 'bg-green-100 text-green-700' : 'bg-[#d8dcee] text-[#141a3d]'
                  }`}>
                    {selectedProduct.stock > 0 ? `Stok: ${selectedProduct.stock}` : 'Stokta Yok'}
                  </span>
                </div>

                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
                  <span>Barkod: <span className="font-mono text-gray-700">{selectedProduct.barcode}</span></span>
                  {selectedProduct.category && <span>Kategori: <span className="text-gray-700">{selectedProduct.category}</span></span>}
                  {selectedProduct.kdv_orani > 0 && <span>KDV: %{selectedProduct.kdv_orani}</span>}
                </div>

                {selectedProduct.description && (
                  <div className="text-sm text-gray-600 border-t pt-3" dangerouslySetInnerHTML={{ __html: selectedProduct.description }} />
                )}

                {/* Add to Cart */}
                {selectedProduct.stock > 0 && (
                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center border border-gray-200 rounded-lg">
                        <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-3 py-2 hover:bg-gray-100 text-lg font-bold">−</button>
                        <input
                          type="number"
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-12 text-center py-2 border-0 focus:ring-0 text-base font-semibold"
                          min="1"
                        />
                        <button onClick={() => setQuantity(quantity + 1)} className="px-3 py-2 hover:bg-gray-100 text-lg font-bold">+</button>
                      </div>
                      <button
                        onClick={() => addToCart(selectedProduct.id, quantity)}
                        disabled={addingToCart === selectedProduct.id}
                        className="flex-1 py-2.5 bg-[#1a224f] text-white rounded-lg font-medium hover:bg-[#141a3d] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                      >
                        {addingToCart === selectedProduct.id ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <>
                            <ShoppingCart className="w-4 h-4" />
                            Sepete Ekle - {formatCurrency(getNetPrice(selectedProduct.price) * quantity)}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
